/**
 * Debounced draft autosave + localStorage restore (PRD FR-1.2). Onboarding must
 * survive a refresh, a bounced tab, or an auth round-trip without asking twice.
 */
import type { DoorCreated, HouseRules } from "./api";

const DRAFT_KEY = "nob.onboarding.draft.v1";
const DOOR_KEY = "nob.onboarding.door.v1";
// `nob.billing.pack.v1` and `nob.pending_autoreload.v1` stood beside this one,
// carrying a chosen pack and an auto-reload rider across Stripe's origin.
// Ruling R-0916-1 deleted both purchases; the subscription's own round trip is
// the only one left, and it is the key below.
const PLAN_KEY = "nob.billing.plan.v1";
const PORTAL_KEY = "nob.billing.portal.v1";
const KNOCK_TARGET_KEY = "nob.knock.target.v1";
const KNOCK_PENDING_KEY = "nob.knock.pending.v1";
const KNOCK_DRAFT_KEY = "nob.knock.draft.v1";
const KNOCK_HELD_KEY = "nob.knock.held.v1";
const ANON_KEY = "nob.anon.v1";

export type Draft = {
  gets: string[];
  doesnt: string[];
  /** Where they were, so a refresh lands them back on the same screen. */
  step: "identity" | "gets" | "doesnt";
};

function read<T>(key: string): T | null {
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null; // private mode, quota, corrupt JSON — never block onboarding
  }
}

function write(key: string, value: unknown): void {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* ignore */
  }
}

function remove(key: string): void {
  try {
    window.localStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}

let timer: ReturnType<typeof setTimeout> | undefined;

export function saveDraft(draft: Draft): void {
  clearTimeout(timer);
  timer = setTimeout(() => write(DRAFT_KEY, draft), 150);
}

export function loadDraft(): Draft | null {
  const d = read<Partial<Draft>>(DRAFT_KEY);
  if (!d) return null;
  const step = d.step === "gets" || d.step === "doesnt" ? d.step : "identity";
  return {
    gets: Array.isArray(d.gets) ? d.gets.filter((s) => typeof s === "string") : [],
    doesnt: Array.isArray(d.doesnt) ? d.doesnt.filter((s) => typeof s === "string") : [],
    step,
  };
}

export function saveDoor(created: DoorCreated): void {
  write(DOOR_KEY, created);
}

export function loadDoor(): DoorCreated | null {
  const d = read<DoorCreated>(DOOR_KEY);
  return d?.door?.handle ? d : null;
}

/** Swaps the latest House Rules into the saved result, leaving the door alone. */
export function saveDoorRules(created: DoorCreated, houseRules: HouseRules): DoorCreated {
  const next: DoorCreated = { ...created, house_rules: houseRules };
  saveDoor(next);
  return next;
}

/**
 * THE ONE ROUND TRIP LEFT (docs/plans.md §10.1). A subscription checkout leaves
 * the origin, so the rung chosen has to survive the trip — not because it is
 * authoritative (the webhook binds the subscription; this browser does not) but
 * so the plan can be named by the screen that comes back rather than described
 * in the abstract.
 *
 * It had two siblings, `savePendingPack` and the auto-reload rider, and ruling
 * R-0916-1 deleted both with the purchases they described. (The paid reset
 * that replaced them never left the browser and is deleted too, ruling
 * R-0923-3 (1).)
 */
export function savePendingPlan(tier: string): void {
  write(PLAN_KEY, tier);
}

export function takePendingPlan(): string | null {
  const tier = read<string>(PLAN_KEY);
  try {
    window.localStorage.removeItem(PLAN_KEY);
  } catch {
    /* ignore */
  }
  return tier === "lite" || tier === "personal" || tier === "pro5" || tier === "pro20"
    ? tier
    : null;
}

/**
 * THE BILLING PORTAL'S ROUND TRIP (wave 87). Replacing a card or correcting
 * the billing details leaves the origin for the processor's portal, which
 * comes back to `/you/plan?portal=1`. What survives the trip is only that one
 * was made, and on which page — enough for the screen that comes back to ask
 * the server to read the customer fresh (`POST /plan/billing-portal/return`)
 * rather than wait for the webhook. Not authoritative: the processor's webhook
 * runs the same sync either way.
 */
export function savePendingPortal(flow: "payment_method" | "details"): void {
  write(PORTAL_KEY, flow);
}

export function takePendingPortal(): "payment_method" | "details" | null {
  const flow = read<string>(PORTAL_KEY);
  remove(PORTAL_KEY);
  return flow === "payment_method" || flow === "details" ? flow : null;
}

/**
 * Knocking (M8). Two separate facts, kept apart on purpose: the *target* is a
 * door someone arrived to knock at, and it has to survive sign-in and a
 * checkout round-trip — both of which leave the origin and come back to a bare
 * `/`. The *pending knock* is what came of it, and is the id the interview loop
 * will reopen. Neither is authoritative: the server owns both.
 */
export function saveKnockTarget(handle: string): void {
  write(KNOCK_TARGET_KEY, handle);
}

export function loadKnockTarget(): string | null {
  const handle = read<string>(KNOCK_TARGET_KEY);
  return typeof handle === "string" && handle.trim() ? handle.trim() : null;
}

export function clearKnockTarget(): void {
  remove(KNOCK_TARGET_KEY);
}

/**
 * What the sender has typed so far. Buying a plan mid-knock leaves the origin
 * for Stripe, so without this the form would come back empty — which would
 * read as the product having lost their answers, because it would have.
 */
export type KnockDraft = {
  handle: string;
  email: string;
  booking: string;
  company: string;
  /**
   * THE WORDS THEMSELVES (owner item 28, 2026-08-26).
   *
   * The knock written INSIDE the console used to be React state and nothing
   * else, on the reasoning that a compose thread never leaves the origin. That
   * reasoning held right up until the thing a Free knocker does at the send: the
   * send is refused, the paywall opens, and buying a plan is a round trip to
   * Stripe — which leaves the origin, comes back to a bare `/`, and used to find
   * the words gone. Somebody paid us money and we lost their sentence.
   *
   * Optional because the at-door intake writes this record too and has no body
   * field of its own; a record without one restores nothing, which is what the
   * intake wants.
   */
  body?: string;
};

export function saveKnockDraft(draft: KnockDraft): void {
  write(KNOCK_DRAFT_KEY, draft);
}

export function loadKnockDraft(): KnockDraft | null {
  const d = read<Partial<KnockDraft>>(KNOCK_DRAFT_KEY);
  if (!d || typeof d.handle !== "string" || !d.handle) return null;
  const str = (value: unknown) => (typeof value === "string" ? value : "");
  return {
    handle: d.handle,
    email: str(d.email),
    booking: str(d.booking),
    company: str(d.company),
    ...(typeof d.body === "string" ? { body: d.body } : {}),
  };
}

let bodyTimer: ReturnType<typeof setTimeout> | undefined;

/**
 * The composer's words, debounced, onto the record the door at this handle
 * already owns. Debounced because it is called on every keystroke and a
 * localStorage write per character is a write per character; MERGED because the
 * record is shared with the at-door intake and clobbering somebody's typed
 * email to save their sentence would be trading one lost draft for another.
 *
 * A different door replaces the record outright: there is one draft at a time in
 * this product, exactly as there is one compose thread.
 */
export function saveKnockBody(handle: string, body: string): void {
  clearTimeout(bodyTimer);
  bodyTimer = setTimeout(() => {
    const current = loadKnockDraft();
    const base: KnockDraft =
      current && current.handle === handle
        ? current
        : { handle, email: "", booking: "", company: "" };
    write(KNOCK_DRAFT_KEY, { ...base, handle, body });
  }, 150);
}

/** The words this browser kept for that door, or "" — never another door's. */
export function loadKnockBody(handle: string): string {
  const draft = loadKnockDraft();
  return draft && draft.handle === handle && typeof draft.body === "string" ? draft.body : "";
}

export function clearKnockDraft(): void {
  // The pending write goes too. A debounced keystroke landing AFTER the send
  // that cleared this would restore a knock somebody has already made.
  clearTimeout(bodyTimer);
  remove(KNOCK_DRAFT_KEY);
}

/**
 * The knock somebody wrote BEFORE they had an account (schema: `held_drafts`).
 *
 * Two copies on purpose, and this is the second one. The server holds the
 * authoritative copy under an anonymous key so the words survive a different
 * browser tab, a different day, and the trip out to X; this one survives a
 * server that will not answer. The server's wins when both are there — it is
 * the copy the send was actually promised against — and this is what stands
 * when it is not.
 */
export type HeldKnock = { handle: string; body: string };

export function saveHeldKnock(held: HeldKnock): void {
  write(KNOCK_HELD_KEY, held);
}

export function loadHeldKnock(): HeldKnock | null {
  const h = read<Partial<HeldKnock>>(KNOCK_HELD_KEY);
  if (!h || typeof h.handle !== "string" || !h.handle) return null;
  const body = typeof h.body === "string" ? h.body : "";
  return body.trim() ? { handle: h.handle, body } : null;
}

/** The words have been put back in somebody's hands; there is nothing left to hold. */
export function clearHeldKnock(): void {
  remove(KNOCK_HELD_KEY);
}

/**
 * The anonymous key a pre-account visit writes its drafts under. Minted once
 * per browser and kept, because the whole point is to be recognisable after a
 * round trip out to an identity provider and back.
 *
 * It is NOT an identity and it authorises nothing: the server's held drafts are
 * text and a 30-day clock, and a knock still costs consent and a plan's
 * allowance like any other. `crypto.randomUUID` where it exists, and a plain
 * random string where it does not — an old browser gets a working draft, not an
 * exception.
 */
export function anonKey(): string {
  const existing = loadAnonKey();
  if (existing) return existing;
  const minted =
    typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
      ? crypto.randomUUID()
      : `anon-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
  write(ANON_KEY, minted);
  return minted;
}

/** The key if this browser has one — never minting one just to look. */
export function loadAnonKey(): string | null {
  const key = read<string>(ANON_KEY);
  return typeof key === "string" && key.trim() ? key : null;
}

export type PendingKnock = { id: string; handle: string };

export function savePendingKnock(knock: PendingKnock): void {
  write(KNOCK_PENDING_KEY, knock);
}

export function loadPendingKnock(): PendingKnock | null {
  const k = read<Partial<PendingKnock>>(KNOCK_PENDING_KEY);
  if (!k || typeof k.id !== "string" || !k.id) return null;
  return { id: k.id, handle: typeof k.handle === "string" ? k.handle : "" };
}

/**
 * Forgotten only when the server no longer knows the knock (M9). A *finished*
 * knock is kept on purpose: re-entering the door should show the transcript and
 * how it ended, and the standing that follows attaches to the same id.
 */
export function clearPendingKnock(): void {
  remove(KNOCK_PENDING_KEY);
}

/**
 * NOTE (owner decision 2026-08-02): the directory-offer dismissal that used to
 * live here — `nob.directory.dismissed.v1`, session storage, "Not now means not
 * this visit" — is gone with the offer itself. Doors are findable by default, so
 * there is nothing to ask and nothing to decline; findability is a setting read
 * from the server, not a prompt whose refusal has to be remembered. The key is
 * deliberately not swept in `clearLocalSession` either: nothing writes it, and a
 * removal of a key no version of this code sets is noise, not hygiene.
 */

export function clearOnboarding(): void {
  clearTimeout(timer);
  try {
    window.localStorage.removeItem(DRAFT_KEY);
    window.localStorage.removeItem(DOOR_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Everything stored about the PERSON, gone — the sign-out sweep. Every key in
 * this file belongs to whoever was signed in (their draft, their door, their
 * half-typed knock), and any of it surviving into the next session would dress
 * the next person in the last one's clothes. A new persisted key is not done
 * until it is either listed here or genuinely person-independent — the same
 * rule resetFakeControls() enforces for mock state on the API side.
 */
export function clearLocalSession(): void {
  clearOnboarding();
  clearKnockTarget();
  clearKnockDraft();
  clearHeldKnock();
  // The anonymous key goes too. It belongs to whoever wrote a knock in this
  // browser before signing in, and the next person inheriting it would inherit
  // the draft it points at — the exact clothes-of-the-last-person problem this
  // sweep exists to prevent.
  remove(ANON_KEY);
  clearPendingKnock();
  takePendingPlan();
  takePendingPortal();
}
